import React, { useState } from "react";
import T from '../../components/common/T';
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useCompliance } from "../../context/ComplianceContext";
import { useAuth } from "../../context/AuthContext";
import DataTable from "../../components/common/DataTables.jsx";
import TableActionButtons from "../../components/common/TableActionButtons";
import { DocumentIcon } from "@heroicons/react/24/outline";

const formatDate = (iso) => (!iso ? "—" : new Date(iso).toLocaleDateString());

// ADDED prop renaming to allow safe fallback to Context data
const ComplianceListView = ({ compliances: propCompliances }) => {
  const navigate = useNavigate();
  // Safe Fallback: Grab compliances from context in case the parent forgot to pass the prop
  const { compliances: contextCompliances = [], deleteCompliance } = useCompliance();
  const { checkAccess } = useAuth();
  const [confirmDelete, setConfirmDelete] = useState(null);

  // The final array to map over
  const dataToShow = (propCompliances && propCompliances.length > 0) ? propCompliances : contextCompliances;

  const handleDelete = (id) => {
    deleteCompliance(id);
    toast.success("Record deleted successfully!", { position: "top-right", autoClose: 2000, theme: "colored" });
    setConfirmDelete(null);
  };

  const columns = [
    {
      header: "Compliance Name",
      accessor: "complianceName",
      sortable: true,
      render: (comp) => (
        <div className="font-semibold text-gray-900 flex items-center gap-2">
          {comp.complianceName}
          {comp.documentUrl && (
            <a href={comp.documentUrl} target="_blank" rel="noreferrer" className="text-blue-500 hover:text-blue-700 transition">
              <DocumentIcon className="w-3.5 h-3.5"/>
            </a>
          )}
        </div>
      )
    },
    {
      header: "Project",
      accessor: "projectName",
      sortable: true,
      render: (comp) => <span className="text-blue-600 font-medium">{comp.projectName || "General"}</span>
    },
    { header: "Authority", accessor: "authorityName", sortable: true },
    {
      header: "Certificate #",
      accessor: "certificateNumber",
      render: (comp) => <span className="font-mono text-gray-500 text-[11px]">{comp.certificateNumber || "—"}</span>
    },
    {
      header: "Expiry Date",
      accessor: "expiryDate",
      sortable: true,
      render: (comp) => {
        const isExpired = new Date(comp.expiryDate) < new Date();
        return (
          <span className={`font-medium ${isExpired ? 'text-red-600' : 'text-gray-700'}`}>
            {formatDate(comp.expiryDate)}
          </span>
        );
      }
    },
    {
      header: "Status",
      accessor: "status",
      align: "center",
      render: (comp) => (
        <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded border ${
          comp.status === "Valid" ? "bg-green-50 text-green-700 border-green-200" :
          comp.status === "Renewal Pending" ? "bg-yellow-50 text-yellow-700 border-yellow-200" :
          "bg-red-50 text-red-700 border-red-200"
        }`}>
          {comp.status}
        </span>
      )
    },
    {
      header: "Actions",
      align: "right",
      render: (comp) => (
        <TableActionButtons
          onEdit={checkAccess('compliance.edit') ? () => navigate(`/admin/compliance-safety/edit/${comp.id}`) : undefined}
          onDelete={checkAccess('compliance.delete') ? () => setConfirmDelete(comp) : undefined}
        />
      )
    }
  ];

  return (
    <div className="relative">
      <DataTable data={dataToShow} columns={columns} emptyMessage="No compliance records found." />

      {/* DELETE CONFIRMATION MODAL */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50 backdrop-blur-sm">
          <div className="bg-white p-6 rounded-xl shadow-lg w-80 text-center animate-fade-in">
            <h2 className="text-base font-bold mb-3 text-gray-800"><T>Delete Record?</T></h2>
            <p className="text-gray-600 mb-5 text-sm"><T>Are you sure you want to delete </T><br/><strong>{confirmDelete.complianceName}</strong>?</p>
            <div className="flex justify-center gap-3">
              <button onClick={() => setConfirmDelete(null)} className="px-4 py-1.5 border border-gray-300 rounded hover:bg-gray-50 text-sm font-medium"><T>Cancel</T></button>
              <button onClick={() => handleDelete(confirmDelete.id)} className="bg-red-600 text-white px-4 py-1.5 rounded hover:bg-red-700 text-sm font-bold shadow-sm"><T>Delete</T></button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComplianceListView;